import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowUpRight, ArrowRight, ShieldCheck, Cpu, Code2, Sparkles, ChevronDown } from 'lucide-react';

const Hero = () => {
  const reduceMotion = useReducedMotion();

  const fadeUp = (delay = 0) => ({
    initial: { opacity: 0, y: reduceMotion ? 0 : 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: reduceMotion ? 0.2 : 0.5, ease: [0.16, 1, 0.3, 1], delay: reduceMotion ? 0 : delay },
  });

  return (
    <section className="relative w-full pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden bg-[#060C1B]">
      {/* Brand logo ambient radial lights */}
      <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-fuchsia-600/12 rounded-full blur-[160px] pointer-events-none animate-glow" />
      <div className="absolute bottom-10 right-1/4 w-[500px] h-[500px] bg-blue-600/12 rounded-full blur-[160px] pointer-events-none animate-glow" style={{ animationDelay: '3s' }} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] bg-cyan-500/6 rounded-full blur-[100px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">

          {/* Left Column: Asymmetric Copy (7 cols) */}
          <div className="lg:col-span-7 flex flex-col text-left">

            {/* Brand Tagline Badge */}
            <motion.div {...fadeUp(0)} className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-slate-900/90 border border-fuchsia-500/30 w-fit mb-6 shadow-md">
              <Sparkles size={14} className="text-fuchsia-400 animate-pulse" />
              <span className="font-mono text-[11px] font-bold tracking-[0.2em] text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-400 via-purple-400 to-cyan-400 uppercase">
                INTELLIGENCE THAT SCALES
              </span>
            </motion.div>

            {/* Main Headline */}
            <motion.h1
              {...fadeUp(0.08)}
              className="font-heading text-4xl sm:text-5xl lg:text-[3.6rem] font-extrabold text-white tracking-tight leading-[1.06] mb-6"
            >
              Enterprise software &amp; AI systems{' '}
              <span className="relative">
                built to{' '}
                <span className="gradient-text">scale your revenue.</span>
              </span>
            </motion.h1>

            {/* Subtitle Value Prop */}
            <motion.p
              {...fadeUp(0.14)}
              className="text-slate-300 text-lg sm:text-xl leading-relaxed mb-8 max-w-2xl font-normal"
            >
              Zynqora Edge designs, engineers, and ships autonomous AI agents, workflow automation pipelines, and custom SaaS products for high-growth businesses in North America and globally.
            </motion.p>

            {/* Solid Action Buttons */}
            <motion.div {...fadeUp(0.2)} className="flex flex-col sm:flex-row gap-4 mb-12">
              <button
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="btn-primary text-base font-semibold px-8 py-4"
              >
                Book Strategy Call
                <ArrowUpRight size={18} strokeWidth={2.5} />
              </button>

              <button
                onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
                className="btn-secondary text-base font-semibold px-8 py-4"
              >
                Explore Services
                <ArrowRight size={18} strokeWidth={2} />
              </button>
            </motion.div>

            {/* Trust & Guarantee Checks */}
            <motion.div {...fadeUp(0.26)} className="pt-8 border-t border-white/10 grid sm:grid-cols-3 gap-4">
              {[
                { title: 'In-House Engineers', desc: 'Zero outsourced code' },
                { title: 'Fixed-Scope Quotes', desc: 'No unpredictable hourly fees' },
                { title: 'Weekly Deployments', desc: 'Continuous production updates' },
              ].map((item) => (
                <div key={item.title} className="flex flex-col">
                  <div className="flex items-center gap-1.5 text-cyan-400 font-heading font-semibold text-sm mb-1">
                    <ShieldCheck size={16} strokeWidth={2.5} />
                    <span>{item.title}</span>
                  </div>
                  <span className="text-xs text-slate-400">{item.desc}</span>
                </div>
              ))}
            </motion.div>

          </div>

          {/* Right Column: Real Photography Visual Frame (5 cols) */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 relative"
          >
            <div className="relative rounded-2xl overflow-hidden border border-white/15 bg-slate-900 shadow-2xl group gradient-border">
              {/* Real Photography Image */}
              <div className="relative aspect-[4/5] sm:aspect-[4/3] lg:aspect-[4/5] w-full overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1200&q=80"
                  alt="Zynqora Edge engineering team collaborating on software architecture"
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                  loading="eager"
                />
                {/* Blend overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#060C1B] via-slate-950/20 to-transparent opacity-90" />
              </div>

              {/* Floating Real-world Engineering Badge */}
              <div className="absolute bottom-6 left-6 right-6 p-4 rounded-xl bg-slate-950/90 backdrop-blur-xl border border-white/15 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-fuchsia-500/10 border border-fuchsia-500/20 flex items-center justify-center text-fuchsia-400 flex-shrink-0">
                    <Code2 size={20} />
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-sm text-white">Full-Cycle Engineering</h4>
                    <p className="text-xs text-slate-400 mt-0.5">Architected under CTO Haris' technical direction.</p>
                  </div>
                </div>
              </div>

              {/* Top Accent Stat Badge */}
              <div className="absolute top-4 right-4 px-3 py-1.5 rounded-lg bg-slate-950/80 backdrop-blur-md border border-white/15 text-[11px] font-mono font-semibold text-slate-200 flex items-center gap-2">
                <Cpu size={14} className="text-cyan-400" />
                <span>Production Ready</span>
              </div>

              {/* Clients served badge */}
              <div className="absolute top-4 left-4 px-3 py-1.5 rounded-lg bg-emerald-500/10 backdrop-blur-md border border-emerald-500/20 text-[11px] font-mono font-semibold text-emerald-300 flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <span>Live Projects</span>
              </div>
            </div>
          </motion.div>

        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-slate-600 hidden lg:flex"
      >
        <span className="text-[10px] font-mono tracking-widest uppercase">Scroll</span>
        <motion.div
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 1.5, ease: 'easeInOut' }}
        >
          <ChevronDown size={16} />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
